import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { logger } from '../utils/logger';
import { FLAG_KEYS, UI_ENTRYPOINT_VARIANTS, INLINE_PDP_VARIANTS, PERSONALIZED_PDP_VARIANTS } from '../../../shared/featureFlags';

// ============================================================
// 🏷️ Type Definitions
// ============================================================

export interface ClientFeatureFlags {
  suggestions: { enabled: boolean; maxCount: number };
  imageGeneration: { enabled: boolean };
  // Entrypoint visibility flags
  entrypoints: {
    pill: boolean;           // Current pill (default: true)
    floatingButton: boolean; // Bottom-right circular button (default: false)
    siteNavButton: boolean;  // Header on desktop, mobile nav on mobile (default: false)
  };
  // Inline PDP chat (separate from entrypoints)
  inlinePDP: { enabled: boolean };
  // Personalized PDP (description reframing + "Why You'll Love This" card)
  personalizedPDP: { enabled: boolean };
}

// Defaults used until experiments are loaded
const DEFAULT_FLAGS: ClientFeatureFlags = {
  suggestions: { enabled: false, maxCount: 5 }, // Default: disabled
  imageGeneration: { enabled: false },          // Default: disabled
  entrypoints: {
    pill: true,           // Default: show the pill
    floatingButton: false, // Default: don't show floating button
    siteNavButton: false,  // Default: don't show site nav button
  },
  inlinePDP: { enabled: false },  // Default: don't show inline PDP chat
  personalizedPDP: { enabled: true },  // Default: enabled for now — switch to PostHog flag for A/B testing later
};

interface FeatureContextValue {
  flags: ClientFeatureFlags;
  updateFlags: (newFlags: Partial<ClientFeatureFlags>) => void;
}

const FeatureContext = createContext<FeatureContextValue>({
  flags: DEFAULT_FLAGS,
  updateFlags: () => {},
});

// Window.CloseraConfig types are in src/types/global.d.ts

interface FeatureProviderProps {
  children: ReactNode;
  experiments?: Record<string, string | boolean>;
}

export function FeatureProvider({ children, experiments: propExperiments }: FeatureProviderProps) {
  const [flags, setFlags] = useState<ClientFeatureFlags>(DEFAULT_FLAGS);

  // Read flags from experiments passed by closera-loader.js (via /experiment/assign)
  useEffect(() => {
    const experiments = propExperiments ?? window.CloseraConfig?._experiments ?? {};

    // Parse boolean feature flags
    const suggestionsFlag = experiments[FLAG_KEYS.SUGGESTIONS];
    const imageGenerationFlag = experiments[FLAG_KEYS.IMAGE_GENERATION];
    const uiEntrypointsVariant = experiments[FLAG_KEYS.UI_ENTRYPOINTS];
    const inlinePDPVariant = experiments[FLAG_KEYS.INLINE_PDP_UI];
    const personalizedPDPVariant = experiments[FLAG_KEYS.PERSONALIZED_PDP];

    // Boolean or multivariate flags: true/"test" = enabled, false/"control"/undefined = disabled
    const suggestionsEnabled = suggestionsFlag === true || suggestionsFlag === 'test';
    const imageGenerationEnabled = imageGenerationFlag === true || imageGenerationFlag === 'test';
    const suggestionsMaxCount = 5; // Always 5

    // Parse ui-entrypoints multivariate flag
    let entrypoints = DEFAULT_FLAGS.entrypoints;

    if (uiEntrypointsVariant && typeof uiEntrypointsVariant === 'string') {
      // Look up variant in config
      const config = UI_ENTRYPOINT_VARIANTS[uiEntrypointsVariant];

      if (config) {
        entrypoints = config;
      } else {
        logger.warn(`[FeatureFlags] Unknown ui-entrypoints variant: ${uiEntrypointsVariant}`);
      }
    }

    // Parse inline-pdp-ui multivariate flag (separate from entrypoints)
    let inlinePDP = DEFAULT_FLAGS.inlinePDP;

    if (inlinePDPVariant && typeof inlinePDPVariant === 'string') {
      const config = INLINE_PDP_VARIANTS[inlinePDPVariant];

      if (config) {
        inlinePDP = config;
      } else {
        logger.warn(`[FeatureFlags] Unknown inline-pdp-ui variant: ${inlinePDPVariant}`);
      }
    }

    // Parse personalized-pdp flag
    let personalizedPDP = DEFAULT_FLAGS.personalizedPDP;

    if (personalizedPDPVariant && typeof personalizedPDPVariant === 'string') {
      const config = PERSONALIZED_PDP_VARIANTS[personalizedPDPVariant];

      if (config) {
        personalizedPDP = config;
      } else {
        logger.warn(`[FeatureFlags] Unknown personalized-pdp variant: ${personalizedPDPVariant}`);
      }
    }

    setFlags({
      suggestions: {
        enabled: suggestionsEnabled,
        maxCount: suggestionsMaxCount,
      },
      imageGeneration: {
        enabled: imageGenerationEnabled,
      },
      entrypoints,
      inlinePDP,
      personalizedPDP,
    });
  }, [propExperiments]);

  const updateFlags = useCallback((newFlags: Partial<ClientFeatureFlags>) => {
    setFlags(prev => ({ ...prev, ...newFlags }));
  }, []);

  return (
    <FeatureContext.Provider value={{ flags, updateFlags }}>
      {children}
    </FeatureContext.Provider>
  );
}

export const useFeatureFlags = () => useContext(FeatureContext);
